import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);
import { PinnedSectionDefaults } from "../base";
import type { AnimationActions, ScrollTriggerProps } from "../types";
import { getStepRange } from "../helpers";
import {
  inProps,
  leftDelay,
  leftEnterStartProps,
  leftInProps,
  leftLeaveEndProps,
  outProps,
  SELECTORS,
  SwapHorizontalAnimationActions,
  SwapSubElements,
} from "./helpers";

const animateEnter = ({
  section,
  left,
  right,
  bg,
  timeline,
}: {
  section: HTMLElement;
  left: HTMLElement;
  right: HTMLElement;
  bg: HTMLElement | undefined;
  timeline: gsap.core.Timeline;
}) => {
  timeline
    .set(left, leftEnterStartProps, 0)
    .to(section, inProps, 0)
    .to(right, inProps, 0);
  if (bg) timeline.to(bg, inProps, 0);
  timeline.to(left, leftInProps, leftDelay);
};

const animateLeave = ({
  section,
  left,
  right,
  bg,
  timeline,
}: {
  section: HTMLElement;
  left: HTMLElement;
  right: HTMLElement;
  bg: HTMLElement | undefined;
  timeline: gsap.core.Timeline;
}) => {
  if (bg) timeline.to(bg, outProps);
  timeline.to(left, leftLeaveEndProps);
  timeline.to(right, outProps);
  timeline.to(section, outProps);
};

const animation: SwapHorizontalAnimationActions = {
  onEnter: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, right, bg } = subElements as SwapSubElements;

    const tl = gsap.timeline();
    animateEnter({ section, left, right, bg, timeline: tl });
  },
  onEnterBack: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, right, bg } = subElements as SwapSubElements;

    const tl = gsap.timeline();
    animateEnter({ section, left, right, bg, timeline: tl });
  },
  onLeave: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, right, bg } = subElements as SwapSubElements;

    const tl = gsap.timeline();
    animateLeave({ section, left, right, bg, timeline: tl });
  },
  onLeaveBack: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, right, bg } = subElements as SwapSubElements;

    const tl = gsap.timeline();
    animateEnter({ section, left, right, bg, timeline: tl });
  },
};

const lastAnimation: Required<
  Pick<AnimationActions, "onLeave" | "onEnterBack">
> = {
  onLeave: ({ subElements }) => {
    if (!subElements) return;
    const { left, right } = subElements as SwapSubElements;
    gsap.timeline().to(left, { opacity: 0, duration: 0.5 }, 0);
    gsap.timeline().to(right, { opacity: 0, duration: 0.5 }, 0);
  },
  onEnterBack: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, right } = subElements as SwapSubElements;
    gsap.timeline().to(section, inProps);
    gsap.timeline().to(left, { opacity: 1, duration: 0.5 });
    gsap.timeline().to(right, { opacity: 1, duration: 0.5 });
  },
};
const firstAnimation: Required<
  Pick<AnimationActions, "onEnter" | "onLeaveBack">
> = {
  onEnter: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, right } = subElements as SwapSubElements;
    const tl = gsap.timeline();

    tl.to(section, { top: "0", ...inProps }, 0)
      .to(right, { top: "0", ...inProps }, 0)
      .set(left, leftEnterStartProps, 0)
      .to(left, leftInProps, leftDelay);
  },
  onLeaveBack: ({ section, subElements }) => {
    if (!subElements) return;
    const { left, right } = subElements as SwapSubElements;
    gsap.timeline().to(section, { top: "100vh", duration: 0.5 });
    gsap.timeline().to(left, leftLeaveEndProps);
    gsap.timeline().to(right, outProps);
  },
};

export const SwapHorizontalScrollTrigger = ({
  timeline,
  section,
  step,
  triggerElement,
}: ScrollTriggerProps) => {
  const media = section.querySelector(SELECTORS.media!) as HTMLElement;
  const info = section.querySelector(SELECTORS.info!) as HTMLElement;
  const bg = section.querySelector(SELECTORS.bg!) as HTMLElement;
  if (!media || !info) throw new Error("Media or info not given");

  const horizontals = triggerElement.querySelectorAll(SELECTORS.horizontals!);
  const slide = section.querySelector(SELECTORS.slide!) as HTMLElement;
  if (!horizontals) throw new Error("No horizontals found");
  const isFirst = slide === horizontals[0];
  const isLast = slide === horizontals[horizontals.length - 1];

  if (isFirst) gsap.timeline().to(section, { opacity: 1 });
  gsap.set(info, leftEnterStartProps);
  const subElements = { left: info, right: media, bg };

  const range = getStepRange(step, 0.1);

  const st = ScrollTrigger.create({
    ...PinnedSectionDefaults,
    trigger: triggerElement,
    pin: false,
    start: () => range.start,
    end: () => range.end,
    onLeave: (self) =>
      isLast
        ? lastAnimation.onLeave({ self, section, timeline, step, subElements })
        : animation.onLeave({ self, section, timeline, step, subElements }),
    onLeaveBack: (self) =>
      isFirst
        ? firstAnimation.onLeaveBack({
            self,
            section,
            timeline,
            step,
            subElements,
          })
        : animation.onLeaveBack({
            self,
            section,
            timeline,
            step,
            subElements,
          }),
    onEnter: (self) =>
      isFirst
        ? firstAnimation.onEnter({ self, section, timeline, step, subElements })
        : animation.onEnter({
            self,
            section,
            timeline,
            step,
            subElements,
          }),
    onEnterBack: (self) =>
      isLast
        ? lastAnimation.onEnterBack({
            self,
            section,
            timeline,
            step,
            subElements,
          })
        : animation.onEnterBack({
            self,
            section,
            timeline,
            step,
            subElements,
          }),
  });
  return st;
};
